import React, { useMemo, useState } from 'react';
import {
  Button,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Alert,
  ListGroup,
  ListGroupItem,
  Spinner,
  FormGroup,
  Input,
} from 'reactstrap';
import {
  useGetDataIntegrationConnectionSchemaConfigQuery,
  useUpdateTableSyncStatusMutation
} from '../../../../../api/initiative-data-integrations';
import { useSiteAlert } from '../../../../../hooks/useSiteAlert';
import { SiteAlertColors } from '../../../../../slice/siteAlertsSlice';

interface ConnectionDetailsPageProps {
  initiativeId: string;
  connectionId: string;
  connectionName: string;
  onClose: () => void;
  initialTab?: 'schema';
}

export const ConnectionDetailsPage: React.FC<ConnectionDetailsPageProps> = ({
  initiativeId,
  connectionId,
  connectionName,
  onClose,
}) => {
  const [pendingTableToggles, setPendingTableToggles] = useState<Record<string, boolean>>({});
  const { addSiteAlert, addSiteError } = useSiteAlert();

  const { data, isLoading, error } = useGetDataIntegrationConnectionSchemaConfigQuery({
    initiativeId,
    connectionId,
  });

  // RTK mutations
  const [updateTableSyncStatus] = useUpdateTableSyncStatusMutation();

  // Get all schemas and their keys
  const schemas = useMemo(() => data?.schemas ? Object.values(data.schemas) : [], [data]);
  const schemaKeys = useMemo(() => data?.schemas ? Object.keys(data.schemas) : [], [data])  ;

  const handleTableToggle = async (schemaKey: string, tableName: string, currentEnabled: boolean) => {
    const tableKey = `${schemaKey}.${tableName}`;

    // Optimistically update local state
    setPendingTableToggles((prev) => ({ ...prev, [tableKey]: !currentEnabled }));

    try {
      // Call API to update table sync status
      await updateTableSyncStatus({
        initiativeId,
        connectionId,
        schemaName: schemaKey,
        tableName,
        enabled: !currentEnabled
      }).unwrap();

      addSiteAlert({
        content: `Table '${tableName}' sync ${!currentEnabled ? 'enabled' : 'disabled'} successfully`,
        color: SiteAlertColors.Success,
      });
    } catch {
      addSiteError({
        content: `Failed to ${!currentEnabled ? 'enable' : 'disable'} sync for table '${tableName}'`,
      });
    } finally {
      // Revert optimistic update on error
      setPendingTableToggles((prev) => {
        const updated = { ...prev };
        delete updated[tableKey];
        return updated;
      });
    }
  };

  const getTableEffectiveState = (schemaKey: string, tableName: string, originalEnabled: boolean) => {
    const tableKey = `${schemaKey}.${tableName}`;
    return Object.prototype.hasOwnProperty.call(pendingTableToggles, tableKey) ? pendingTableToggles[tableKey] : originalEnabled;
  };

  const isTableTogglePending = (schemaKey: string, tableName: string) => {
    const tableKey = `${schemaKey}.${tableName}`;
    return Object.prototype.hasOwnProperty.call(pendingTableToggles, tableKey);
  };

  // Calculate total stats across all schemas
  const totalStats = useMemo(() => {
    let totalTables = 0;
    let totalEnabled = 0;

    schemas.forEach(schema => {
      if (schema.tables) {
        const schemaTables = Object.values(schema.tables);
        totalTables += schemaTables.length;
        totalEnabled += schemaTables.filter(table => table.enabled).length;
      }
    });

    return { totalTables, totalEnabled };
  }, [schemas]);

  return (
    <div>
      {/* Header */}
      <Button color='link' onClick={onClose} className='me-3'>
        <i className='fal fa-arrow-left me-2' />
        Back to connections
      </Button>
      <h3>
        <i className='fal fa-layer-group me-2 text-primary' />
        {connectionName}
      </h3>

      <Card>
        <CardHeader className='d-flex align-items-center justify-content-between'>
          <div>
            {totalStats.totalTables > 0 && (
              <div className='mt-1'>
                <Badge color='success' className='me-2'>
                  {totalStats.totalEnabled} tables syncing
                </Badge>
                <Badge color='secondary'>{totalStats.totalTables - totalStats.totalEnabled} tables disabled</Badge>
              </div>
            )}
          </div>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <div className='text-center my-4'>
              <Spinner color='primary' /> Loading schemas...
            </div>
          ) : error ? (
            <Alert color='danger'>
              <i className='fal fa-exclamation-triangle me-2' />
              Failed to load schemas.
            </Alert>
          ) : !schemas.length ? (
            <div className='text-muted'>No schema information available.</div>
          ) : (
            <div>
              {schemaKeys.map((schemaKey) => {
                const schema = data?.schemas?.[schemaKey];
                const tables = schema?.tables ? Object.values(schema.tables) : [];
                const enabledTables = tables.filter(table => table.enabled).length;

                return (
                  <div key={schemaKey} className='mb-4'>
                    <div className='d-flex align-items-center justify-content-between mb-3'>
                      <h6 className='mb-0'>
                        <i className='fal fa-database me-2' />
                        Schema: {schemaKey}
                        {schema?.name_in_destination && schema.name_in_destination !== schemaKey && (
                          <span className='text-muted ms-2'>
                            {'=>'} {schema.name_in_destination}
                          </span>
                        )}
                      </h6>
                      <div>
                        <Badge color='success' className='me-2'>
                          {enabledTables} enabled
                        </Badge>
                        <Badge color='secondary'>{tables.length - enabledTables} disabled</Badge>
                      </div>
                    </div>

                    {tables.length === 0 ? (
                      <div className='text-muted'>No tables found in this schema.</div>
                    ) : (
                      <ListGroup flush>
                        {tables.map((table) => {
                          const effectiveEnabled = getTableEffectiveState(schemaKey, table.name_in_destination, table.enabled);
                          const isPending = isTableTogglePending(schemaKey, table.name_in_destination);
                          const canToggle = table.enabled_patch_settings?.allowed !== false;

                          return (
                            <ListGroupItem
                              key={table.name_in_destination}
                              style={{ cursor: 'pointer', borderRadius: 4 }}
                              className='d-flex align-items-center justify-content-between'
                            >
                              <div className='d-flex align-items-center flex-grow-1'>
                                <i
                                  className={`fas ${effectiveEnabled ? 'fa-check text-success' : 'fa-times text-danger'} me-2`}
                                />
                                <i className='fal fa-table me-2' />
                                <span className='me-2'>{table.name_in_destination}</span>
                              </div>
                              <div>
                                {table.sync_mode && (
                                  <Badge color='secondary' className='me-2'>
                                    {table.sync_mode === 'SOFT_DELETE'
                                      ? 'Replicate'
                                      : table.sync_mode === 'HISTORY'
                                        ? 'History'
                                        : table.sync_mode}
                                  </Badge>
                                )}
                              </div>
                              <div className='d-flex align-items-center' onClick={(e) => e.stopPropagation()}>
                                {isPending && <Spinner size='sm' color='primary' className='me-2' />}
                                <FormGroup switch className='mb-0'>
                                  <Input
                                    type='switch'
                                    checked={effectiveEnabled}
                                    disabled={!canToggle || isPending}
                                    onChange={() => handleTableToggle(schemaKey, table.name_in_destination, table.enabled)}
                                    title={
                                      canToggle
                                        ? 'Toggle table sync'
                                        : table.enabled_patch_settings?.reason || 'Cannot toggle sync for this table'
                                    }
                                  />
                                </FormGroup>
                              </div>
                            </ListGroupItem>
                          );
                        })}
                      </ListGroup>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};
